/**
 * Clustered validation report for the work-pattern import flow (#426).
 *
 * Replaces WorkPatternImportTable when the `enable-work-pattern-clustered-report`
 * feature flag is ON. Renders:
 *   - A plain-language banner (blocked / warnings-only / all-pass)
 *   - Four tabs: All (default), Errors, Warnings, Passed
 *   - Collapsible clusters, each grouping rows by issue type
 *   - Row accordions with WorkPatternCycleGrid (AC-25) + warning actions (AC-18/19)
 *   - A footer: Cancel import (left) · Confirm import / Re-validate (right); after
 *     commit, Import new work patterns (clears the batch like Cancel import)
 *
 * Build from PRD rules and ACs — the prototype is design reference only.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { Button } from "@datapay-by-datacom/design-system";
import type { WorkPatternRow } from "../../../generated/core-api";
import { WorkflowActionBar } from "../../../shared";
import type {
  WorkPatternImportResult,
  WorkPatternImportParseFailedRow,
} from "../../hooks/WorkPatternImport/useWorkPatternImportInvocation";
import { WorkPatternCycleGrid } from "./WorkPatternCycleGrid";
import {
  clusterRows,
  groupRowsByClusters,
  countTabs,
  getVisibleGroups,
  filterGroupsByNameSearch,
  firstClusterIdForTab,
  formatBlockedImportBanner,
  WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID,
  type ClusterId,
  type ClusterGroup,
  type ClusteredRow,
  type TabId,
} from "./workPatternImportClustering";
import styles from "./WorkPatternImportReport.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WorkPatternImportReportProps {
  patterns: WorkPatternRow[];
  /** The 200 envelope from a validate-only or commit call. Always present when this component renders. */
  result: WorkPatternImportResult;
  /** Which phase produced the current result — drives header text and available footer actions. */
  resultPhase: "validation" | "commit" | undefined;
  /** Per-row StandardHours/StandardDays mismatch warnings from the CSV parser. */
  warnings?: Map<number, string[]>;
  /** Indices whose warnings the user dismissed via Ignore (R14). */
  ignoredWarningIndices?: ReadonlySet<number>;
  /** When true, Confirm import is hidden until the user re-validates. */
  validationStale?: boolean;
  /** Parse-failed CSV rows merged into the validation report. */
  parseFailedRows?: WorkPatternImportParseFailedRow[];
  /** CSV row numbers aligned with `patterns` (row 2 = first data row). */
  patternCsvRowNumbers?: number[];
  /** Row-level client parse errors aligned with validated patterns. */
  parseErrors?: string[];
  onClearAll: () => void;
  onValidate: () => void;
  /** Opens the commit confirmation modal in the parent (#401). */
  onRequestCommitConfirm?: () => void;
  /** Shown beside Confirm import when the user tries to commit with blocking errors. */
  commitBlockedMessage?: string | null;
  onEditPattern?: (index: number) => void;
  onIgnoreWarning?: (index: number) => void;
  /** Validate-only workflow in progress (re-validate / edit-save). */
  isValidating?: boolean;
  /** When true, Cancel import is disabled so an in-flight commit cannot be abandoned. */
  isCommitting?: boolean;
  /** Non-API work (for example CSV replacement parsing) that locks report actions. */
  isProcessing?: boolean;
  /**
   * When true, the "Download errors CSV" button is rendered (#414).
   * False when there are no per-row failures (warnings-only, all-pass, or
   * file-level errors only).
   */
  canDownloadErrors?: boolean;
  /** Called when the user presses "Download errors CSV" (#414). */
  onDownloadErrors?: () => void;
  /**
   * Temporary gate for Edit in UI on blocking-error rows plus the new
   * edit-modal fields (#435). Default/undefined = today's behaviour (no Edit
   * on error rows). Remove once GA — see `FeatureFlag.EnableWorkPatternErrorEditInUi`.
   */
  enableErrorEditInUi?: boolean;
  /** Enables converged sticky actions and unified partial-import feedback. */
  redesignEnabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert ISO YYYY-MM-DD → DD/MM/YYYY for display. Passes through unchanged if not ISO. */
function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_IGNORED = new Set<number>();
const TABS: TabId[] = ["all", "errors", "warnings", "passed"];

const TAB_LABELS: Record<TabId, string> = {
  all: "All",
  errors: "Errors",
  warnings: "Warnings",
  passed: "Passed",
};

function rowKey(row: ClusteredRow): string {
  return `${row.clusterId}-${row.csvRowNumber}-${row.workPatternName}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkPatternImportReport({
  patterns,
  result,
  resultPhase,
  warnings,
  ignoredWarningIndices,
  validationStale,
  parseFailedRows,
  patternCsvRowNumbers,
  parseErrors,
  onClearAll,
  onValidate,
  onRequestCommitConfirm,
  commitBlockedMessage,
  onEditPattern,
  onIgnoreWarning,
  isValidating = false,
  isCommitting = false,
  isProcessing = false,
  canDownloadErrors = false,
  onDownloadErrors,
  enableErrorEditInUi = false,
  redesignEnabled = true,
}: WorkPatternImportReportProps) {
  const ignored = ignoredWarningIndices ?? EMPTY_IGNORED;

  // ---------------------------------------------------------------------------
  // Clustering (pure computation, memoised)
  // ---------------------------------------------------------------------------

  const allRows = useMemo(
    () =>
      clusterRows({
        patterns,
        result,
        warnings,
        ignoredWarningIndices: ignored,
        parseErrors,
        parseFailedRows,
        patternCsvRowNumbers,
      }),
    [
      patterns,
      result,
      warnings,
      ignored,
      parseErrors,
      parseFailedRows,
      patternCsvRowNumbers,
    ]
  );

  const allGroups = useMemo(() => groupRowsByClusters(allRows), [allRows]);
  const tabCounts = useMemo(() => countTabs(allRows), [allRows]);

  const batchKey = useMemo(
    () =>
      [
        patterns.map((pattern) => pattern.workPatternName).join("\0"),
        parseFailedRows?.length ?? 0,
      ].join("|"),
    [patterns, parseFailedRows]
  );

  const resultKey = useMemo(
    () =>
      [
        resultPhase ?? "",
        result.total,
        result.succeeded,
        result.failed,
        result.errors.length,
      ].join("|"),
    [result, resultPhase]
  );

  // ---------------------------------------------------------------------------
  // UI state
  // ---------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<TabId>("all");
  const [nameSearch, setNameSearch] = useState("");
  // Single trimmed term drives filtering, the hint gate, and the no-match copy
  // so they can never disagree about whitespace-only input (#428 review).
  const trimmedNameSearch = nameSearch.trim();

  useEffect(() => {
    setNameSearch("");
  }, [batchKey, resultKey]);

  // Open the first cluster synchronously on initial render (no effect needed).
  const [openClusterIds, setOpenClusterIds] = useState<ReadonlySet<ClusterId>>(
    () => {
      const first = allGroups[0]?.config.id;
      return first ? new Set([first]) : new Set<ClusterId>();
    }
  );

  const [openRowKey, setOpenRowKey] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasBlockingErrors = tabCounts.errors > 0;
  const showValidationBlockedBanner =
    resultPhase === "validation" && hasBlockingErrors;

  // Warning actions (Edit / Dismiss warning) are only offered when:
  //   • in validation phase (not post-commit)
  //   • parent provided the callbacks
  // Note: blocking errors in other rows do NOT suppress actions on warning
  // rows — error-cluster rows already skip this action block entirely.
  const canOfferWarningActions =
    resultPhase === "validation" &&
    onEditPattern !== undefined &&
    onIgnoreWarning !== undefined;

  const hasUnacknowledgedWarnings = tabCounts.warnings > 0;

  const visibleGroups = useMemo(
    () => getVisibleGroups(activeTab, allGroups),
    [activeTab, allGroups]
  );

  const filteredGroups = useMemo(
    () => filterGroupsByNameSearch(visibleGroups, trimmedNameSearch),
    [visibleGroups, trimmedNameSearch]
  );

  const hasRowsInActiveTab = useMemo(
    () => visibleGroups.some((group) => group.rows.length > 0),
    [visibleGroups]
  );

  // Row totals scoped to the active tab (same scope as visibleGroups) so the
  // search hint's "of Y" reflects the current tab, not the whole upload (#428).
  const visibleRowCount = useMemo(
    () => visibleGroups.reduce((sum, group) => sum + group.rows.length, 0),
    [visibleGroups]
  );
  const filteredRowCount = useMemo(
    () => filteredGroups.reduce((sum, group) => sum + group.rows.length, 0),
    [filteredGroups]
  );

  // Full row count per cluster (tab-filtered, not search-filtered) so the
  // cluster chip always reflects the true count in the file (#428 option B).
  const clusterTotalCount = useMemo(
    () => new Map(visibleGroups.map((g) => [g.config.id, g.rows.length])),
    [visibleGroups]
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleTabChange = useCallback(
    (tab: TabId) => {
      setActiveTab(tab);
      setOpenRowKey(null);
      // Auto-open the first cluster for the new tab (design review delta).
      const firstId = firstClusterIdForTab(tab, allGroups);
      if (firstId !== null) {
        setOpenClusterIds(new Set([firstId]));
      }
    },
    [allGroups]
  );

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, tab: TabId) => {
      const currentIndex = TABS.indexOf(tab);
      let nextIndex: number | null = null;
      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % TABS.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = TABS.length - 1;
      }
      if (nextIndex === null) return;

      event.preventDefault();
      const nextTab = TABS[nextIndex];
      handleTabChange(nextTab);
      document.getElementById(`work-pattern-tab-${nextTab}`)?.focus();
    },
    [handleTabChange]
  );

  const handleClusterToggle = useCallback((id: ClusterId) => {
    setOpenClusterIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleRowToggle = useCallback((key: string) => {
    setOpenRowKey((prev) => (prev === key ? null : key));
  }, []);

  // ---------------------------------------------------------------------------
  // Banner
  // ---------------------------------------------------------------------------

  const bannerText =
    resultPhase === "commit"
      ? `Import results: ${result.succeeded} of ${result.total} succeeded.`
      : showValidationBlockedBanner
        ? formatBlockedImportBanner(
            tabCounts.errors,
            tabCounts.all,
            redesignEnabled
              ? tabCounts.passed + tabCounts.warnings
              : tabCounts.passed
          )
        : hasUnacknowledgedWarnings
          ? `${tabCounts.warnings} warning${tabCounts.warnings === 1 ? "" : "s"} · ${tabCounts.passed} passed · 0 failed. Check each warning before confirming import.`
          : `${tabCounts.all} of ${tabCounts.all} patterns passed.`;

  // ---------------------------------------------------------------------------
  // Footer button visibility
  // ---------------------------------------------------------------------------

  const allSucceeded = !hasBlockingErrors && result.failed === 0;
  const showRevalidate =
    (redesignEnabled || allSucceeded) &&
    (validationStale ?? false) &&
    resultPhase !== "commit";
  const showConfirm =
    !validationStale &&
    resultPhase !== "commit" &&
    onRequestCommitConfirm !== undefined;
  const confirmDisabled =
    isValidating || isCommitting || (redesignEnabled && isProcessing);
  const rowActionsDisabled =
    redesignEnabled && (isValidating || isCommitting || isProcessing);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={styles.container}>
      {/* Banner */}
      <div
        className={`${styles.banner} ${
          showValidationBlockedBanner
            ? styles.bannerError
            : resultPhase === "commit" && result.failed > 0
              ? styles.bannerWarn
              : hasUnacknowledgedWarnings
                ? styles.bannerWarn
                : styles.bannerSuccess
        }`}
        role={showValidationBlockedBanner ? "alert" : "status"}
      >
        {bannerText}
      </div>

      {/* Tabs */}
      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Filter validation report by status"
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            id={`work-pattern-tab-${tab}`}
            role="tab"
            type="button"
            aria-selected={activeTab === tab}
            aria-controls={`work-pattern-panel-${tab}`}
            tabIndex={activeTab === tab ? 0 : -1}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
            onClick={() => handleTabChange(tab)}
            onKeyDown={(event) => handleTabKeyDown(event, tab)}
          >
            {TAB_LABELS[tab]} ({tabCounts[tab]})
          </button>
        ))}
      </div>

      <div
        id={`work-pattern-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`work-pattern-tab-${activeTab}`}
        tabIndex={0}
      >
        {/* Name search — shown when the active tab has rows (not filteredGroups) */}
        {hasRowsInActiveTab && (
          <div className={styles.nameSearchWrapper}>
            <input
              type="search"
              className={styles.nameSearch}
              placeholder="Search work pattern names…"
              aria-label="Search work pattern names"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
            {trimmedNameSearch !== "" && (
              <p className={styles.nameSearchHint}>
                Showing {filteredRowCount} of {visibleRowCount} uploaded work
                patterns
              </p>
            )}
          </div>
        )}

        {/* Cluster list */}
        <div className={styles.clusters}>
          {filteredGroups.map((group) => (
            <ClusterSection
              key={group.config.id}
              group={group}
              isOpen={openClusterIds.has(group.config.id)}
              onToggle={() => handleClusterToggle(group.config.id)}
              openRowKey={openRowKey}
              onRowToggle={handleRowToggle}
              hasBlockingErrors={hasBlockingErrors}
              canOfferWarningActions={canOfferWarningActions}
              errorCount={tabCounts.errors}
              totalRowCount={
                clusterTotalCount.get(group.config.id) ?? group.rows.length
              }
              onEditPattern={onEditPattern}
              onIgnoreWarning={onIgnoreWarning}
              rowActionsDisabled={rowActionsDisabled}
              enableErrorEditInUi={enableErrorEditInUi}
            />
          ))}
          {filteredGroups.length === 0 && (
            <p className={styles.empty}>
              {hasRowsInActiveTab && trimmedNameSearch
                ? `No patterns matching "${trimmedNameSearch}"`
                : "No rows in this view."}
            </p>
          )}
        </div>
      </div>

      {/* Persistent workflow actions; after commit, start a new import. */}
      {redesignEnabled && resultPhase === "commit" && (
        <WorkflowActionBar
          ariaLabel="Work pattern import actions"
          leadingActions={
            <Button
              variant="secondary"
              onPress={onClearAll}
              isDisabled={isProcessing}
            >
              Import new work patterns
            </Button>
          }
          trailingActions={
            canDownloadErrors && onDownloadErrors ? (
              <Button
                variant="secondary"
                onPress={onDownloadErrors}
                isDisabled={isProcessing}
              >
                Download issues CSV
              </Button>
            ) : undefined
          }
        />
      )}
      {redesignEnabled && resultPhase !== "commit" && (
        <WorkflowActionBar
          ariaLabel="Work pattern import actions"
          status={
            commitBlockedMessage && showConfirm ? (
              <div
                id={WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID}
                className={`${styles.banner} ${styles.bannerError}`}
                role="alert"
              >
                {commitBlockedMessage}
              </div>
            ) : undefined
          }
          leadingActions={
            <Button
              variant="secondary"
              onPress={onClearAll}
              isDisabled={isCommitting || isProcessing}
            >
              Cancel import
            </Button>
          }
          trailingActions={
            <div className={styles.footerRight}>
              {canDownloadErrors && onDownloadErrors && (
                <Button
                  variant="secondary"
                  onPress={onDownloadErrors}
                  isDisabled={isProcessing}
                >
                  Download issues CSV
                </Button>
              )}
              {showRevalidate && (
                <Button
                  variant="primary"
                  onPress={onValidate}
                  isDisabled={isValidating || isCommitting || isProcessing}
                >
                  {isValidating ? "Processing..." : "Re-validate"}
                </Button>
              )}
              {showConfirm && (
              <Button
                  variant="primary"
                  onPress={onRequestCommitConfirm}
                  isDisabled={confirmDisabled}
                  aria-describedby={
                    commitBlockedMessage
                      ? WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID
                      : undefined
                  }
              >
                  {isCommitting ? "Importing..." : "Confirm import"}
              </Button>
              )}
            </div>
          }
        />
      )}
      {!redesignEnabled && resultPhase === "commit" && (
        <footer className={styles.legacyFooter}>
          <div className={styles.legacyFooterActions}>
            <Button variant="secondary" onPress={onClearAll}>
              Import new work patterns
            </Button>
            {canDownloadErrors && onDownloadErrors && (
              <Button variant="secondary" onPress={onDownloadErrors}>
                Download issues CSV
              </Button>
            )}
          </div>
        </footer>
      )}
      {!redesignEnabled && resultPhase !== "commit" && (
        <footer className={styles.legacyFooter}>
          {commitBlockedMessage && showConfirm && (
            <div
              id={WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID}
              className={`${styles.banner} ${styles.bannerError}`}
              role="alert"
            >
              {commitBlockedMessage}
            </div>
          )}
          <div className={styles.legacyFooterActions}>
            <Button
              variant="secondary"
              onPress={onClearAll}
              isDisabled={isCommitting}
            >
              Cancel import
            </Button>
            <div className={styles.footerRight}>
              {canDownloadErrors && onDownloadErrors && (
                <Button variant="secondary" onPress={onDownloadErrors}>
                  Download issues CSV
                </Button>
              )}
              {showRevalidate && (
                <Button
                  variant="primary"
                  onPress={onValidate}
                  isDisabled={isValidating || isCommitting}
                >
                  {isValidating ? "Processing..." : "Re-validate"}
                </Button>
              )}
              {showConfirm && (
                <Button
                  variant="primary"
                  onPress={onRequestCommitConfirm}
                  isDisabled={confirmDisabled}
                  aria-describedby={
                    commitBlockedMessage
                      ? WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID
                      : undefined
                  }
                >
                  {isCommitting ? "Importing..." : "Confirm import"}
                </Button>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClusterSection sub-component
// ---------------------------------------------------------------------------

interface ClusterSectionProps {
  group: ClusterGroup;
  isOpen: boolean;
  onToggle: () => void;
  openRowKey: string | null;
  onRowToggle: (key: string) => void;
  hasBlockingErrors: boolean;
  canOfferWarningActions: boolean;
  errorCount: number;
  /** Unfiltered row count for this cluster (ignores name search). Used for the chip. */
  totalRowCount: number;
  onEditPattern?: (index: number) => void;
  onIgnoreWarning?: (index: number) => void;
  rowActionsDisabled: boolean;
  /** Temporary gate for Edit in UI on blocking-error rows (#435). */
  enableErrorEditInUi?: boolean;
}

function ClusterSection({
  group,
  isOpen,
  onToggle,
  openRowKey,
  onRowToggle,
  hasBlockingErrors,
  canOfferWarningActions,
  errorCount,
  totalRowCount,
  onEditPattern,
  onIgnoreWarning,
  rowActionsDisabled,
  enableErrorEditInUi,
}: ClusterSectionProps) {
  const { config, rows } = group;
  const isPassed = config.kind === "passed";

  // Dynamic title/hint for the passed cluster.
  const title = isPassed
    ? hasBlockingErrors
      ? `${totalRowCount} ready to import`
      : `${totalRowCount} passed`
    : config.title;

  const hint = isPassed
    ? hasBlockingErrors
      ? `These will be imported. The ${errorCount} row${errorCount === 1 ? "" : "s"} with errors will be skipped.`
      : "These rows can commit with the file."
    : config.hint;

  return (
    <article className={`${styles.cluster} ${styles[`cluster_${config.kind}`]}`}>
      <button
        type="button"
        className={styles.clusterHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`${title}, ${totalRowCount} row${totalRowCount === 1 ? "" : "s"}, ${isOpen ? "expanded" : "collapsed"}`}
      >
        <div className={styles.clusterTitleGroup}>
          <span className={styles.clusterTitle}>{title}</span>
          {hint && <span className={styles.clusterHint}>{hint}</span>}
        </div>
        <span
          className={`${styles.countChip} ${styles[`countChip_${config.kind}`]}`}
          data-testid="cluster-count"
        >
          {totalRowCount}
        </span>
      </button>

      {isOpen && (
        <div className={styles.clusterRows}>
          {rows.map((row) => {
            const key = rowKey(row);
            return (
              <RowItem
                key={key}
                rowKey={key}
                row={row}
                clusterKind={config.kind}
                isOpen={openRowKey === key}
                onToggle={() => onRowToggle(key)}
                hasBlockingErrors={hasBlockingErrors}
                canOfferWarningActions={canOfferWarningActions}
                errorCount={errorCount}
                onEditPattern={onEditPattern}
                onIgnoreWarning={onIgnoreWarning}
                rowActionsDisabled={rowActionsDisabled}
                enableErrorEditInUi={enableErrorEditInUi}
              />
            );
          })}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// RowItem sub-component
// ---------------------------------------------------------------------------

interface RowItemProps {
  rowKey: string;
  row: ClusteredRow;
  clusterKind: "error" | "warning" | "passed";
  isOpen: boolean;
  onToggle: () => void;
  hasBlockingErrors: boolean;
  canOfferWarningActions: boolean;
  errorCount: number;
  onEditPattern?: (index: number) => void;
  onIgnoreWarning?: (index: number) => void;
  rowActionsDisabled: boolean;
  /** Temporary gate for Edit in UI on blocking-error rows (#435). */
  enableErrorEditInUi?: boolean;
}

function RowItem({
  row,
  clusterKind,
  isOpen,
  onToggle,
  hasBlockingErrors,
  canOfferWarningActions,
  errorCount,
  onEditPattern,
  onIgnoreWarning,
  rowActionsDisabled,
  enableErrorEditInUi,
}: RowItemProps) {
  // Reason shown inline on the row button (short description below the name).
  const inlineReason =
    clusterKind === "error"
      ? row.errorMessage
      : clusterKind === "warning"
      ? row.warningMessages?.join(" · ")
      : undefined;

  const pillLabel =
    clusterKind === "error" ? "Error" : clusterKind === "warning" ? "Warning" : "Pass";

  return (
    <div className={styles.rowItem}>
      <button
        type="button"
        className={`${styles.rowBtn} ${isOpen ? styles.rowBtnOpen : ""}`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`Row ${row.csvRowNumber}, ${row.workPatternName}, ${pillLabel}, ${isOpen ? "expanded" : "collapsed"}`}
      >
        <span className={styles.rowChev} aria-hidden="true">
          {isOpen ? "▼" : "▶"}
        </span>
        <span className={styles.rowNum}>{row.csvRowNumber}</span>
        <span className={styles.rowName}>
          <span className={styles.rowNamePrimary} data-testid="row-name-primary">
            {row.workPatternName}
          </span>
          {inlineReason && (
            <span className={styles.rowReason}>{inlineReason}</span>
          )}
        </span>
        <span
          className={`${styles.pill} ${styles[`pill_${clusterKind}`]}`}
          aria-label={pillLabel}
        >
          {pillLabel}
        </span>
      </button>

      {isOpen && (
          <div className={styles.accordion}>
            {/* Pattern name + metadata — anchors the user when the grid scrolls */}
            <div className={styles.accordionMeta}>
              <span className={styles.accordionPatternName}>{row.workPatternName}</span>
              <span className={styles.accordionMetaLine}>
                Row {row.csvRowNumber}
                {row.pattern && (
                  <>
                    {" · "}{row.pattern.cycleDays}-day cycle from {formatDate(row.pattern.startDate)}
                    {" · "}Std hours {row.pattern.standardHours}
                  </>
                )}
              </span>
            </div>

            {/* Blocked note — error rows while file is blocked.
                Only shown when there is a cycle grid below to preview;
                parse-failed rows have no pattern so show just the error text.
                When Edit in UI is available (#435, flag-gated), the copy is
                softened to point at Edit in UI as the in-portal fix path;
                parse-failed rows and the flag-off case keep the original
                "fix the file and upload again" wording. */}
            {clusterKind === "error" && hasBlockingErrors && row.pattern && (
            <p className={styles.noteError}>
              {enableErrorEditInUi && canOfferWarningActions ? (
                <>
                  You can use this preview to see what&apos;s wrong.{" "}
                  <strong>
                    Use Edit in UI below to fix this row, or fix the file and upload again.
                  </strong>
                </>
              ) : (
                <>
                  You can use this preview to see what&apos;s wrong.{" "}
                  <strong>This row won&apos;t be imported until you fix it and upload again.</strong>
                </>
              )}
            </p>
          )}

          {/* Held note — passed rows imported alongside skipped error rows */}
          {clusterKind === "passed" && hasBlockingErrors && (
            <p className={styles.noteHeld}>
              This pattern passed validation. It will be imported. The{" "}
              {errorCount} row{errorCount === 1 ? "" : "s"} that{" "}
              {errorCount === 1 ? "fails" : "fail"} will be skipped.
            </p>
          )}

          {/* Co-reported warning on an error row (AC-19) */}
          {clusterKind === "error" &&
            row.warningMessages &&
            row.warningMessages.length > 0 && (
              <p className={styles.noteWarn}>
                <strong>Warning also present:</strong>{" "}
                {row.warningMessages.join(" ")}
              </p>
            )}

          {/* Warning messages above the grid for warning rows */}
          {clusterKind === "warning" &&
            row.warningMessages &&
            row.warningMessages.length > 0 && (
              <p className={styles.noteWarn}>
                {row.warningMessages.join(" ")}
              </p>
            )}

          {/* Cycle grid — only for rows backed by a WorkPatternRow (AC-25).
              Mark overflow days red for dayHours error rows so the user can
              see exactly which day cell caused the validation failure. */}
          {row.pattern ? (
            <WorkPatternCycleGrid
              pattern={row.pattern}
              hideHeader
              embedded
              markOverflowDays={row.clusterId === "dayHours"}
            />
          ) : (
            // Parse-failed rows have no pattern data — show the parse error.
            <p className={styles.parseErrorText}>{row.errorMessage}</p>
          )}

          {/* Warning actions: Edit in UI + Ignore (AC-18; hidden when errors exist per AC-19) */}
          {clusterKind === "warning" &&
            canOfferWarningActions &&
            row.patternIndex >= 0 && (
              <div className={styles.warningActions}>
                <Button
                  variant="secondary"
                  onPress={() => onEditPattern!(row.patternIndex)}
                  isDisabled={rowActionsDisabled}
                >
                  Edit in UI
                </Button>
                <Button
                  variant="secondary"
                  onPress={() => onIgnoreWarning!(row.patternIndex)}
                  isDisabled={rowActionsDisabled}
                >
                  Dismiss warning
                </Button>
              </div>
            )}

          {/* Error-row Edit in UI (#435, flag-gated): no Dismiss warning here —
              blocking errors are not dismissible, only fixable via the modal. */}
          {enableErrorEditInUi &&
            clusterKind === "error" &&
            canOfferWarningActions &&
            row.pattern &&
            row.patternIndex >= 0 && (
              <div className={styles.warningActions}>
                <Button
                  variant="secondary"
                  onPress={() => onEditPattern!(row.patternIndex)}
                  isDisabled={rowActionsDisabled}
                >
                  Edit in UI
                </Button>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
